import { useRef, useState } from "react";
import PropTypes from "prop-types";

import { Avatar, Button, ButtonVariant, CenterModal, ModalSize } from "@repo/design-system/components/ui";
import { FormBuilder } from "@repo/design-system/components/forms";
import { onResetForm } from "@repo/design-system/components/forms/utils";
import { buildProfilePhotoUploadConfig, ProfilePhotoUploadConfig } from "./config/ProfilePhotoUpload.config";

// Service's
import { ToastService } from "@gyandiksha/shared-ui";
import { useGetProfilePhotoQuery, useUploadProfilePhotoMutation } from "@gyandiksha/shared-ui/app/services";

const ProfilePhotoAvatar = ({ fullName, fallbackSrc }) => {

  // State's
  const formRef = useRef(null);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [formConfig, setFormConfig] = useState(buildProfilePhotoUploadConfig);

  // API Call's
  const { data: profilePhotoData } = useGetProfilePhotoQuery();
  const [uploadProfilePhoto, { isLoading: isUploading }] = useUploadProfilePhotoMutation();

  // Handler's
  const handleCloseModal = () => {
    onResetForm(formConfig, setFormConfig, formConfig.initialState || ProfilePhotoUploadConfig.initialState);
    setIsUploadModalOpen(false);
  };

  // Photo refetches automatically — the upload invalidates the ProfilePhoto tag
  const handleUpload = async () => {
    const formData = formRef.current?.getValidatedFormData();
    if (!formData) return;

    const result = await uploadProfilePhoto(formData.profilePicture?.file);
    if (result?.data) {
      ToastService.success({ title: "Uploaded", description: "Profile picture uploaded successfully" });
      handleCloseModal();
    }
  };

  return (
    <>
      <div className="sp-avatar-wrapper">
        <Avatar
          src={profilePhotoData?.data?.url || fallbackSrc}
          alt={fullName}
          size="large"
          shape="circle"
        />
        <Button
          className="sp-avatar-edit-btn"
          leftIcon="tabler:edit-filled"
          ttLabel="Upload picture"
          targetId="profilePhotoUpload"
          variant={ButtonVariant.PRIMARY}
          onClick={() => setIsUploadModalOpen(true)}
        />
      </div>

      <CenterModal
        isOpen={isUploadModalOpen}
        modalTitle="Update Your Profile Picture"
        handleToggleModal={handleCloseModal}
        modelSizeClass={ModalSize.WD25}
        footerChildren={
          <>
            <Button variant={ButtonVariant.OUTLINE} leftIcon="material-symbols:close-rounded" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button loading={isUploading} leftIcon="material-symbols:add-a-photo" onClick={handleUpload}>
              Upload
            </Button>
          </>
        }
      >
        <FormBuilder ref={formRef} config={formConfig} />
      </CenterModal>
    </>
  );
};

ProfilePhotoAvatar.propTypes = {
  fullName: PropTypes.string,
  fallbackSrc: PropTypes.string,
};

export default ProfilePhotoAvatar;
